import type {
  EvaluationContext,
  JsonValue,
  Provider,
  ResolutionDetails,
} from '@openfeature/web-sdk';
import {
  ErrorCode,
  OpenFeatureEventEmitter,
  ProviderEvents,
  ProviderStatus,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { LDClient, LDFlagChangeset } from 'launchdarkly-js-client-sdk';

import { translateContext } from './translateContext';
import { translateResult } from './translateResult';

const wrongType = <T>(value: T): ResolutionDetails<T> => ({
  value,
  reason: StandardResolutionReasons.ERROR,
  errorCode: ErrorCode.TYPE_MISMATCH,
});

const makeResolver =
  <T>(typeName: string) =>
  (client: LDClient) =>
  (flagKey: string, defaultValue: T): ResolutionDetails<T> => {
    const res = client.variationDetail(flagKey, defaultValue);
    if (typeof res.value === typeName) return translateResult<T>(res);
    return wrongType(defaultValue);
  };

const resolveBoolean = makeResolver<boolean>('boolean');
const resolveString = makeResolver<string>('string');
const resolveNumber = makeResolver<number>('number');

const resolveObject =
  (client: LDClient) =>
  <T extends JsonValue>(flagKey: string, defaultValue: T): ResolutionDetails<T> => {
    const res = client.variationDetail(flagKey, defaultValue);
    if (res.value !== null && typeof res.value === 'object') return translateResult<T>(res);
    return wrongType(defaultValue);
  };

type IdentifiableContext = EvaluationContext & { targetingKey: string };

// Without a targeting key translateContext yields an anonymous user, which would replace
// the deliberately non-anonymous boot context the app starts LaunchDarkly with.
const isIdentifiable = (context: EvaluationContext | undefined): context is IdentifiableContext =>
  typeof context?.targetingKey === 'string' && context.targetingKey.length > 0;

export const createLaunchDarklyClientProvider = (client: LDClient): Provider => {
  const events = new OpenFeatureEventEmitter();
  let status: ProviderStatus = ProviderStatus.NOT_READY;
  // OpenFeature hands initialize() the context set before the provider was registered, but a
  // context change can still land while we await the client. An identifiable context that
  // gets there first wins, so we never replace a signed-in user with the stale pre-auth one.
  // Held as the in-flight promise rather than a flag so initialize can tell "already done"
  // from "tried and failed" — the latter has to fall back, or nothing identifies at all.
  let identifying: Promise<void> | null = null;

  client.on('change', (changeset: LDFlagChangeset) => {
    events.emit(ProviderEvents.ConfigurationChanged, {
      flagsChanged: Object.keys(changeset),
    });
  });

  const identify = async (context?: EvaluationContext): Promise<void> => {
    if (!isIdentifiable(context)) return;
    const pending = client.identify(translateContext(context)).then(() => undefined);
    identifying = pending;
    await pending;
  };

  // A newer context change can supersede the identify we are waiting on, so follow the chain
  // rather than judging by the first one alone: only the most recent request decides whether
  // the fallback is needed. Falling back while a newer one is still in flight would risk
  // applying the stale pre-auth context after it.
  const latestIdentifyLanded = async (): Promise<boolean> => {
    let awaited: Promise<void> | null = null;
    while (identifying !== null && identifying !== awaited) {
      awaited = identifying;
      if (
        await awaited.then(
          () => true,
          () => false,
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const initialize = async (context?: EvaluationContext): Promise<void> => {
    try {
      await client.waitForInitialization();
      if (!(await latestIdentifyLanded())) {
        // A failed identify leaves flags on the boot context, which is degraded but usable —
        // far better than failing the provider and dropping every flag to its code default.
        await identify(context).catch((err: unknown) => {
          console.error('[createLaunchDarklyClientProvider] client.identify failed:', err);
        });
      }
      status = ProviderStatus.READY;
      events.emit(ProviderEvents.Ready);
    } catch (err) {
      status = ProviderStatus.ERROR;
      events.emit(ProviderEvents.Error, { message: String(err) });
      throw err;
    }
  };

  const onContextChange = async (
    _old: EvaluationContext,
    next: EvaluationContext,
  ): Promise<void> => {
    await identify(next);
  };

  return {
    metadata: { name: 'trm-launchdarkly-client-provider' },
    events,
    get status() {
      return status;
    },
    set status(s: ProviderStatus) {
      status = s;
    },
    initialize,
    onContextChange,
    resolveBooleanEvaluation: resolveBoolean(client),
    resolveStringEvaluation: resolveString(client),
    resolveNumberEvaluation: resolveNumber(client),
    resolveObjectEvaluation: resolveObject(client),
  };
};
